/**
 * cart-tracker.js v2 — bulletproof Chatwoot ↔ Shopify cart sync
 *
 * Install in ZipCushions Shopify theme:
 *   Online Store > Themes > Edit code
 *     Assets > cart-tracker.js
 *   Layout > theme.liquid > add before </body>:
 *     {{ 'cart-tracker.js' | asset_url | script_tag }}
 *
 * ARCHITECTURE NOTE — why this is one-shot install:
 *   The JS sends BOTH:
 *     - pubsub_token: best-guess parsed source_id from cookie
 *     - cookie_raw:   the raw cookie value
 *   The server tries to extract source_id from `pubsub_token` first; if that
 *   fails (e.g. Chatwoot changes cookie format in a future version) it falls
 *   back to decoding `cookie_raw` server-side. This means future Chatwoot
 *   upgrades won't require theme re-uploads — just bot_server updates.
 */

(function () {
  'use strict';

  // =========================================================================
  // CONFIG — replace with your bot_server's public URL
  // =========================================================================
  var FLASK_SYNC_URL = 'https://chat.cozycornerpatios.com/bot/cart-tracker/sync';
  var FLASK_PAGEVIEW_URL = 'https://chat.cozycornerpatios.com/bot/page-tracker/sync';

  var DEBOUNCE_MS = 600;

  // =========================================================================
  // HELPERS
  // =========================================================================

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
    return m ? decodeURIComponent(m[3]) : null;
  }

  /**
   * Extract source_id from a Chatwoot cw_conversation cookie value.
   * Tries every known format. Returns null if none work — the server-side
   * fallback (cookie_raw) will handle it instead.
   */
  function extractSourceId(raw) {
    if (!raw) return null;

    // Format 1: JWT with payload {source_id, inbox_id, exp, iat}
    if (raw.split('.').length === 3) {
      try {
        var payload = raw.split('.')[1];
        // base64url -> base64
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        while (payload.length % 4) payload += '=';
        var decoded = JSON.parse(atob(payload));
        if (decoded.source_id) return decoded.source_id;
        if (decoded.pubsub_token) return decoded.pubsub_token;
      } catch (e) { /* fall through */ }
    }

    // Format 2: JSON object
    if (raw.charAt(0) === '{') {
      try {
        var parsed = JSON.parse(raw);
        return parsed.source_id || parsed.pubsub_token || parsed.token || null;
      } catch (e) { /* fall through */ }
    }

    // Format 3: looks like a plain UUID
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
      return raw;
    }

    return null;
  }

  function getChatwootIdentifier() {
    return (window.$chatwoot && window.$chatwoot.identifier) || null;
  }

  function getChatwootEmail() {
    return (window.$chatwoot && window.$chatwoot.user && window.$chatwoot.user.email) || null;
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed: ' + r.status);
      return r.json();
    });
  }

  function pushSnapshot(reason) {
    var rawCookie = getCookie('cw_conversation');
    if (!rawCookie) return; // Chatwoot widget hasn't set its cookie yet

    var sourceId = extractSourceId(rawCookie);

    fetchCart().then(function (cart) {
      var payload = {
        // Best-guess client-side parsed token. Server prefers this.
        pubsub_token: sourceId,
        // Raw cookie as fallback. If pubsub_token is null OR the server's
        // cart→conversation matching fails, the server can decode this itself.
        cookie_raw: rawCookie,
        identifier: getChatwootIdentifier(),
        email: getChatwootEmail(),
        cart: cart,
        shop_domain: window.location.host,
        reason: reason,
        client_version: 'v2',
      };

      if (reason === 'unload' && navigator.sendBeacon) {
        var blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(FLASK_SYNC_URL, blob);
      } else {
        fetch(FLASK_SYNC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(function () { /* silent — never break shopper UX */ });
      }
    }).catch(function () { /* silent */ });
  }

  var debounceTimer = null;
  function pushDebounced(reason) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      pushSnapshot(reason);
    }, DEBOUNCE_MS);
  }

  // =========================================================================
  // TRIGGER 1: Widget ready — initial sync
  // =========================================================================
  window.addEventListener('chatwoot:ready', function () {
    pushSnapshot('widget_ready');
  });

  if (window.$chatwoot && window.$chatwoot.hasLoaded) {
    setTimeout(function () { pushSnapshot('widget_already_ready'); }, 500);
  }

  // =========================================================================
  // TRIGGER 2: Cart mutations — intercept fetch + XHR to Shopify cart endpoints
  // =========================================================================
  var CART_MUTATION_PATHS = [
    '/cart/add', '/cart/update', '/cart/change', '/cart/clear',
    '/cart/add.js', '/cart/update.js', '/cart/change.js', '/cart/clear.js',
  ];

  function isCartMutation(url) {
    if (!url) return false;
    var s = typeof url === 'string' ? url : (url.url || '');
    for (var i = 0; i < CART_MUTATION_PATHS.length; i++) {
      if (s.indexOf(CART_MUTATION_PATHS[i]) !== -1) return true;
    }
    return false;
  }

  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function () {
      var urlArg = arguments[0];
      var p = origFetch.apply(this, arguments);
      if (isCartMutation(urlArg)) {
        p.then(function () { pushDebounced('cart_mutation_fetch'); }).catch(function () { });
      }
      return p;
    };
  }

  if (window.XMLHttpRequest) {
    var OrigXHR = window.XMLHttpRequest;
    var origOpen = OrigXHR.prototype.open;
    var origSend = OrigXHR.prototype.send;

    OrigXHR.prototype.open = function (method, url) {
      this.__ccpCartMutation = isCartMutation(url);
      return origOpen.apply(this, arguments);
    };

    OrigXHR.prototype.send = function () {
      var self = this;
      if (this.__ccpCartMutation) {
        this.addEventListener('loadend', function () {
          if (self.status >= 200 && self.status < 400) {
            pushDebounced('cart_mutation_xhr');
          }
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  // =========================================================================
  // TRIGGER 3: Page unload — last-chance sync
  // =========================================================================
  window.addEventListener('pagehide', function () {
    pushSnapshot('unload');
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      pushSnapshot('visibility_hidden');
    }
  });

  // =========================================================================
  // PAGE VIEW TRACKING — fires once on page load
  // Sends URL, title, referrer, timestamp. Server-side dedups within 30 sec.
  // Stored per-customer (pubsub_token), full lifetime history.
  // =========================================================================
  function pushPageView() {
    var rawCookie = getCookie('cw_conversation');
    if (!rawCookie) return;  // No Chatwoot session yet — skip
    var sourceId = extractSourceId(rawCookie);

    var payload = {
      pubsub_token: sourceId,
      cookie_raw: rawCookie,
      identifier: getChatwootIdentifier(),
      email: getChatwootEmail(),
      url: window.location.href,
      path: window.location.pathname,
      title: document.title || null,
      referrer: document.referrer || null,
      shop_domain: window.location.host,
      client_version: 'v3',
      timestamp_client: new Date().toISOString()
    };

    try {
      fetch(FLASK_PAGEVIEW_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function () { });
    } catch (e) { /* silent */ }
  }

  // Fire on page load. Poll for cw_conversation cookie up to 12 seconds.
  // This is more robust than fixed timeouts since Chatwoot widget load timing
  // varies wildly (slow networks, cold cache, browser back/forward, etc).
  var pageViewSent = false;
  function tryPushPageView() {
    if (pageViewSent) return;
    var cookie = getCookie('cw_conversation');
    if (cookie) {
      pageViewSent = true;
      pushPageView();
    }
  }

  // Try immediately if Chatwoot already loaded
  if (window.$chatwoot && window.$chatwoot.hasLoaded) {
    setTimeout(tryPushPageView, 100);
  }
  // Try when Chatwoot fires ready event
  window.addEventListener('chatwoot:ready', function () {
    setTimeout(tryPushPageView, 100);
  });
  // Polling fallback — every 250ms for up to 12 seconds
  var pollAttempts = 0;
  var pollMax = 48;  // 48 * 250ms = 12 sec
  var pollInterval = setInterval(function () {
    pollAttempts++;
    tryPushPageView();
    if (pageViewSent || pollAttempts >= pollMax) {
      clearInterval(pollInterval);
    }
  }, 250);
  // Final fallback: also try when page is about to unload (catches very slow loads)
  window.addEventListener('beforeunload', function () {
    tryPushPageView();
  });
})();